/**
 * DetailHero — the editorial header for the per-hospital detail screen.
 *
 * Brings the detail page up to the overview's bar (DESIGN.md): the same
 * eyebrow + beating heart-mark, the big serif title, and — the fact the old
 * header omitted — a HERO METRIC stating how many people are waiting *here,
 * right now*, with a heartbeat whose rate encodes this hospital's busyness and
 * a congestion chip + freshness badge.
 *
 * Pure and prop-driven: the connected page derives `total`/`level`/`measuredAt`
 * from the wait + history responses (see lib/detail.ts) and injects `now`.
 */

import { useState } from "react";
import { CONGESTION_LABEL, type CongestionLevel } from "@/lib/congestion";
import { beatDuration, beatDurationStyle, beatPhaseStyle } from "@/lib/busyness";
import { useCountUp } from "@/lib/countUp";
import { isMorphing } from "@/lib/morph";
import { formatRomeClock } from "@/lib/time";
import { detailLede } from "@/lib/detail";
import { HeartMark } from "./HeartMark";
import { FreshnessBadge } from "./FreshnessBadge";

interface DetailHeroProps {
  slug: string;
  nameIt: string | undefined;
  nameDe: string | undefined;
  /** Current people-waiting total, or `null` before a reading has arrived. */
  total: number | null;
  level: CongestionLevel;
  /** ISO reading instant for the freshness badge. */
  measuredAt: string | null;
  /** Injectable clock for deterministic tests; defaults to the real now. */
  now?: Date | undefined;
}

export function DetailHero({
  slug,
  nameIt,
  nameDe,
  total,
  level,
  measuredAt,
  now,
}: DetailHeroProps) {
  const title = nameIt ?? slug;
  const hasData = total !== null;
  const beatDur = beatDuration(total ?? 0, level);
  // Arrived via the shared-element morph? Freeze the decision at mount. The morph
  // IS the reveal, so suppress both the number count-up and the one-shot rise-in
  // (a rise would visibly reset the beating heart after the morph settles).
  const [arrivedViaMorph] = useState(() => isMorphing());
  const animated = useCountUp(total ?? 0, 1100, arrivedViaMorph);
  const unit = total === 1 ? "persona in attesa adesso" : "persone in attesa adesso";
  const timeLabel = formatRomeClock(now ?? new Date());

  return (
    <header
      className={arrivedViaMorph ? "detail-hero detail-hero--no-rise" : "detail-hero"}
      data-morph-hero
    >
      <p className="hero__eyebrow">
        {/* No live reading ⇒ a static mark, never a false "live" pulse (matches /status). */}
        <span
          className={hasData ? "live-mark-wrap live-ring" : "live-mark-wrap"}
          style={beatDurationStyle(beatDur)}
        >
          <HeartMark size={9} className={hasData ? "heart-mark" : ""} />
        </span>
        Pronto Soccorso · oggi {timeLabel}
      </p>

      <div className="detail-hero__headrow">
        <div className="detail-hero__titles">
          <h1 className="detail__title" data-morph="name">
            {title}
          </h1>
          {nameDe && <p className="detail-hero__name-de">{nameDe}</p>}
        </div>
        <span className={`level-chip level-chip--${level}`}>{CONGESTION_LABEL[level]}</span>
      </div>

      <p className="hero__lede">{detailLede(level, title)}</p>

      {hasData ? (
        <div className="hero__metric">
          <div
            className="hero__big"
            aria-label={`${total} ${unit}`}
            style={{ ...beatDurationStyle(beatDur), ...beatPhaseStyle(slug) }}
          >
            <span
              className="live-mark-wrap live-ring"
              style={beatDurationStyle(beatDur)}
              data-morph="heart"
            >
              <HeartMark size={30} className="heart-mark" />
            </span>
            <span className="hero__number serif tnum" aria-hidden="true" data-morph="number">
              {animated}
            </span>
            <span className="hero__unit">{unit}</span>
          </div>
          <div className="hero__meta">
            <FreshnessBadge iso={measuredAt} now={now} />
          </div>
        </div>
      ) : (
        <p className="detail-hero__nodata">
          Nessun dato in tempo reale al momento — in attesa della prossima rilevazione.
        </p>
      )}
    </header>
  );
}
